import { motion } from "framer-motion";
import { ChevronDown, Sparkles } from "lucide-react";

import AuthAlertMessage from "../components/layout/AuthAlertMessage";
import AuthDivider from "../components/layout/AuthDivider";
import SocialAuthButtons from "../components/layout/SocialAuthButtons";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../components/ui/card";

const AuthShell = ({
	title,
	description,
	footerPrompt,
	footerActionLabel,
	onFooterAction,
	toggleTitle = "Email & Password",
	toggleDescription,
	toggleIcon,
	showEmailForm,
	onToggleEmailForm,
	allowEmailPassword,
	allDisabled,
	error,
	emailDisabledMessage,
	allowGit,
	allowGoogle,
	allowLinkedIn,
	onGithub,
	onGoogle,
	isLoading,
	preFormContent,
	children,
}) => {
	return (
		<div className="relative flex h-min items-center justify-center overflow-hidden backdrop-blur-sm border border-card/50">
			<div className="absolute inset-0">
				<div className="absolute left-[-10%] top-[-10%] h-[400px] w-[400px] rounded-full bg-primary/10 blur-3xl" />
				<div className="absolute bottom-[-10%] right-[-10%] h-[400px] w-[400px] rounded-full bg-blue-500/10 blur-3xl" />
			</div>

			<div
				className="relative z-10 w-full max-w-md bg-card/80 rounded-md"
			>
				<Card className="border border-white/10">
					<CardHeader className="space-y-4 pb-4 text-center">
						<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl">
							<Sparkles className="h-8 w-8 text-primary" />
						</div>

						<div>
							<CardTitle className="text-3xl font-bold tracking-tight">{title}</CardTitle>
							<CardDescription className="mt-2 text-base text-muted-foreground">
								{description}
							</CardDescription>
						</div>
					</CardHeader>

					<CardContent className="space-y-6 rounded-md">
						{error ? <AuthAlertMessage>{error}</AuthAlertMessage> : null}

						{allDisabled ? (
							<AuthAlertMessage tone="warning">
								All authentication methods are currently disabled.
							</AuthAlertMessage>
						) : null}

						<SocialAuthButtons
							allowGit={allowGit}
							allowGoogle={allowGoogle}
							allowLinkedIn={allowLinkedIn}
							onGithub={onGithub}
							onGoogle={onGoogle}
							isLoading={isLoading}
						/>

						<AuthDivider />

						{preFormContent}

						<button
							type="button"
							onClick={() => onToggleEmailForm((value) => !value)}
							className="group flex w-full items-center justify-between rounded-2xl border border-border/60 bg-secondary/60 px-5 py-4 transition-all duration-200 hover:border-primary/30 hover:bg-secondary"
						>
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
									{toggleIcon}
								</div>

								<div className="text-left">
									<p className="font-medium">{toggleTitle}</p>
									<p className="text-xs text-muted-foreground">{toggleDescription}</p>
								</div>
							</div>

							<ChevronDown
								className={`h-5 w-5 transition-transform duration-200 ${showEmailForm ? "rotate-180" : ""}`}
							/>
						</button>

						{showEmailForm && allowEmailPassword ? children : null}

						{!allowEmailPassword && !allDisabled ? (
							<AuthAlertMessage tone="warning">{emailDisabledMessage}</AuthAlertMessage>
						) : null}
					</CardContent>

					<CardFooter className="flex justify-center border-t border-border/50 py-6">
						<p className="text-sm text-muted-foreground">
							{footerPrompt}
							<button type="button" onClick={onFooterAction} className="ml-2 font-semibold text-primary hover:opacity-80">
								{footerActionLabel}
							</button>
						</p>
					</CardFooter>
				</Card>
			</div>
		</div>
	);
};

export default AuthShell;